import { useCallback, useMemo, useState } from 'react';
import { defaultsDeep, filter, merge, pick } from 'lodash';
import clsx from 'clsx';
import { Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Trans } from '@lingui/macro';

import { getChartColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { useNavigate } from '../../../utilities/PgmUtilities';
import { getLogger } from '../../../utilities/UseLog';
import { useFilterState } from '../../../stores/FilterStateStore';
import {
    encodeState as filterEncodeState,
    getUrlParamFromEncodedState as filterGetUrlParamFromEncodedState,
} from '../../../utilities/FilterStateUrlUtilities';
import { getUrlParamFromState as pageGetUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import {
    siteFilterId,
    statusFilterId,
    testFilterId,
    timeRangeComparisonFilterId,
    timeRangeFilterId,
} from '../../filters';
import { CUSTOM_ID } from '../../filters/time-range-filter/TimeRangeFilterConstants';
import { TAB_PERFORMANCE } from '../../panels/test-detail-panel/TestDetailPanel';

import Chart from '../chart/Chart';
import MessageOverlay from '../message-overlay/MessageOverlay';
import { getYAxis } from '../performance-chart/funcs/ChartOptions';

import { TestComparisonTooltip } from '../tooltip/types/TestComparisonTooltip';
import Tooltip from '../../tooltip/Tooltip';
import {
    BEFORE,
    VARIANT_OVERLAY,
    plotOptions,
    getNumberPoints,
    getYAxisTitle,
    highlightAssociatedPoints,
    unhighlightAssociatedPoints,
} from './TestComparisonChartHelper';
import { useStyles } from './TestComparisonChartStyles';

// eslint-disable-next-line no-unused-vars
const log = getLogger('TestComparisonChart');

export const testComparisonDataSource = {
    url: '/query/report/before-after',
    params: {
        renderAsEntity: true,
        type: 'agent,test',
        metrics: '*',
        sort: 'test.name',
        limit: 10,
    },
};

/**
 *
 * @param {*} chartData
 * @param String variant - breakdown or overlay
 */
export function useTestComparisonChartConfig(chartData, variant) {
    return useMemo(() => {
        if (!chartData) return undefined;

        return {
            chartType: 'column',
            chart: {
                animation: false,
                marginLeft: 85,
                marginRight: 0,
                reflow: false,
                spacingBottom: 0,
                spacingLeft: 0,
                spacingRight: 0,
                spacingTop: 24,
                style: {
                    fontFamily: 'Roboto, "sans-serif"',
                },
                snap: true,
                type: 'column',
            },
            credits: {
                enabled: false,
            },
            plotOptions: {
                series:
                    variant === VARIANT_OVERLAY
                        ? {
                              animation: false,
                              grouping: false,
                              shadow: false,
                              pointPlacement: null,
                              stacking: null,
                              groupPadding: null,
                              borderWidth: 0,
                              pointPadding: 0.2,
                              states: {
                                  inactive: {
                                      opacity: 1,
                                  },
                              },
                          }
                        : {
                              animation: false,
                              grouping: true,
                              stacking: 'normal',
                              pointPlacement: null,
                              groupPadding: 0.3,
                              pointPadding: 0.1,
                              states: {
                                  inactive: {
                                      opacity: 1,
                                  },
                              },
                          },
            },
            title: {
                enabled: false,
                text: null,
            },
            xAxis: {
                type: 'category',
                labels: {
                    autoRotation: [-20, -30, -45, -60, -90],
                    autoRotationLimit: 90,
                    padding: 5,
                },
            },
            tooltip: {
                enabled: false,
            },
            chartTrends: chartData.map((series, index) => {
                return {
                    color: series.color,
                    dataIndex: index,
                    formatter: FORMATTER_TYPES.DECIMAL,
                    label: series.stack,
                    multiplier: series?.testMeta?.primaryMetric?.multiplier,
                    units: series?.testMeta?.primaryMetric?.units,
                    key: 0,
                };
            }),
        };
    }, [chartData, variant]);
}

function decorateSeriesHandlers(chartDef, data, pointEvents, seriesEvents) {
    if (!chartDef) return undefined;

    return data.map((item) => {
        return {
            ...item,
            events: seriesEvents,
            point: {
                events: pointEvents,
            },
        };
    });
}

function getXAxisCategories(data) {
    const firstSeries = data[0]?.data || [];
    return firstSeries.map((test) => test?.name);
}

export function TestComparisonChart(props) {
    const { chartDef, className, data, initialHeight, variant } = props;
    const classes = useStyles();
    const theme = useTheme();
    const [tooltipPoint, setTooltipPoint] = useState(null); // the point we're hovering near
    const navigate = useNavigate();

    const [filterState] = useFilterState();

    const clickHandler = useCallback(
        (event) => {
            event.preventDefault();

            // We use event.target.point for a click on the chart point, or event.point for a click on the data label:
            const point = event?.target?.point || event?.point;
            const testId = point?.testMeta?.testId;

            // The only filters applicable to test-detail are test (which we set), site, & status.
            // Also grab the time range comparison filter so we can convert it:
            const newFilterState = pick(filterState.current, [
                siteFilterId,
                statusFilterId,
                timeRangeComparisonFilterId,
            ]);
            newFilterState[testFilterId] = {
                uuid: testId,
            };
            const trcf = newFilterState[timeRangeComparisonFilterId];
            if (trcf.before.start && trcf.after.end) {
                newFilterState[timeRangeFilterId] = {
                    // make the time range cover all of both our before & after time ranges
                    id: CUSTOM_ID,
                    start: trcf.before.start,
                    end: trcf.after.end,
                };
            }
            delete newFilterState[timeRangeComparisonFilterId];
            // Go to the performance tab, since we're coming from a performance chart:
            const encodedNewPageState = pageGetUrlParamFromState({ 'TestDetailPanel-Tab': TAB_PERFORMANCE });
            const encodedNewFilterState = filterGetUrlParamFromEncodedState(filterEncodeState(newFilterState));

            navigate(`/test-detail?${encodedNewFilterState}&${encodedNewPageState}`);
            return false;
        },
        [filterState, navigate]
    );

    // eslint-disable-next-line no-unused-vars
    const legendClickHandler = useCallback((event) => {
        const series = event.target;
        const { chart } = series;
        const isVisible = series.visible;

        // don't let highcharts do this, we're handling it from here
        event.preventDefault();

        const { metricId } = series.options;
        const assocSeries = filter(chart.series, { options: { showInLegend: false, stack: BEFORE, metricId } });

        series.setVisible(!isVisible, false);

        assocSeries.forEach((s) => {
            s.setVisible(!isVisible, false);
        });

        chart.redraw(false);

        return false;
    }, []);

    const options = useMemo(() => {
        if (!chartDef || !data) return undefined;

        const newOptions = {};
        const yAxesWithTitle = [
            {
                title: {
                    text: getYAxisTitle(data[0]?.testMeta),
                },
            },
        ];
        const adjustedYAxes = getYAxis(false, yAxesWithTitle, undefined, theme.palette.type);

        defaultsDeep(newOptions, chartDef, {
            chart: {
                ...chartDef.chart,
                className,
                backgroundColor: theme.palette.background.paper,
            },
            legend: merge(
                {
                    enabled: true,
                    backgroundColor: theme.palette.background.paper,
                    alignColumns: false,
                },
                chartDef?.legendCursor !== false
                    ? {}
                    : {
                          itemHoverStyle: {
                              color: getChartColor('itemHoverStyle', theme.palette.type),
                          },
                          itemStyle: {
                              cursor: 'default',
                          },
                      }
            ),
            plotOptions,
            series: decorateSeriesHandlers(
                chartDef,
                data,
                {
                    mouseOver: (ev) => {
                        const point = ev?.target || null;
                        setTooltipPoint(point);
                        highlightAssociatedPoints(point);
                    },
                    mouseOut: (ev) => {
                        const point = ev?.target;
                        setTooltipPoint(null);
                        unhighlightAssociatedPoints(point);
                    },
                    click: clickHandler,
                },
                {
                    legendItemClick: legendClickHandler,
                }
            ),
            xAxis: {
                ...chartDef.xAxis,
                categories: getXAxisCategories(data),
                tickWidth: 1,
            },
            yAxis: adjustedYAxes,
        });
        return newOptions;
    }, [className, clickHandler, legendClickHandler, theme, chartDef, data]);

    // log.debug('TestComparisonChart options:');
    // log.debugTable(options);

    const numDataPoints = useMemo(() => {
        if (!data) return 0;
        return getNumberPoints(data);
    }, [data]);

    if (!options) return <Box height={initialHeight} />;

    return (
        <div className={clsx('flex-container', classes.chartContainer)}>
            {numDataPoints === 0 && <MessageOverlay message={<Trans>No data available</Trans>} />}
            <Tooltip
                className="flex-container"
                open={!!tooltipPoint}
                offset={60}
                placement="left"
                title={<TestComparisonTooltip chartDef={chartDef} point={tooltipPoint} variant={variant} />}
            >
                <Chart
                    className={clsx('flex-container', classes.chart)}
                    initialHeight={initialHeight}
                    options={options}
                />
            </Tooltip>
        </div>
    );
}
